import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { toast } from "sonner";
import { api, errMsg } from "@/lib/api";
import { useAuth, useMembers } from "@/context/AuthContext";
import { Field, PageHeader, Spinner } from "@/components/kit";
import { DOC_TYPES, uploadDoc } from "@/components/Documents";
import { inr, today } from "@/lib/format";

const MODES = ["Bank Transfer", "UPI", "Cash", "Cheque", "Other"];

export default function SettlementForm() {
  const [sp] = useSearchParams();
  const nav = useNavigate();
  const { gid } = useAuth();
  const { members } = useMembers();
  const editId = sp.get("edit");
  const [f, setF] = useState({ from_person_id: sp.get("from") || "", to_person_id: sp.get("to") || "", amount: sp.get("amount") || "", date: today(),
    payment_mode: "Bank Transfer", reference: "", notes: "", holding_id: sp.get("holding") || "" });
  const [position, setPosition] = useState(null);
  const [holdings, setHoldings] = useState([]);
  const [file, setFile] = useState(null);
  const [docType, setDocType] = useState("Bank Transfer Screenshot");
  const [editComment, setEditComment] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    api.get(`/groups/${gid}/ledger`).then((r) => setPosition(r.data.position));
    api.get(`/groups/${gid}/holdings`).then((r) => setHoldings(r.data));
    if (editId) {
      api.get(`/settlements/${editId}`).then(({ data: s }) => setF({ from_person_id: s.from_person_id, to_person_id: s.to_person_id, amount: s.amount, date: s.date,
        payment_mode: s.payment_mode, reference: s.reference || "", notes: s.notes || "", holding_id: s.holding_id || "" }));
    }
  }, [gid, editId]);

  const up = (k) => (e) => setF({ ...f, [k]: e.target.value });
  const active = members.filter((m) => m.status === "active");
  const sideOf = (pid) => members.find((m) => m.person_id === pid)?.side_karta_id;
  const cross = f.from_person_id && f.to_person_id && sideOf(f.from_person_id) !== sideOf(f.to_person_id);

  const submit = async () => {
    setBusy(true);
    try {
      const body = { ...f, holding_id: f.holding_id || null, edit_comment: editComment };
      const { data } = editId ? await api.put(`/settlements/${editId}`, body) : await api.post(`/groups/${gid}/settlements`, body);
      if (file) {
        try { await uploadDoc("settlement", data.id, file, docType); } catch (e) { toast.error(`Saved, but upload failed: ${errMsg(e)}`); }
      }
      toast.success(data.status === "confirmed" ? "Settlement recorded" : "Settlement submitted for confirmation");
      nav(`/settlement/${data.id}`, { replace: true });
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  if (!members.length) return <Spinner />;
  const opt = (m) => <option key={m.person_id} value={m.person_id}>{m.full_name} ({m.hs_id}) · {m.side_name.split(" ")[0]} side</option>;
  return (
    <div className="pb-6">
      <PageHeader title={editId ? "Edit settlement" : "Record settlement"} subtitle="Settlements reduce balances — they never change ownership" />
      {position && (
        <div className="hd-card bg-[#F9ECE8] border-0 mb-5" data-testid="settlement-current-position">
          <div className="text-xs text-[#B34832] font-semibold">Current position</div>
          <div className="font-bold mt-0.5">{position.summary.direction === "settled" ? "All settled" : `${position.summary.text} ${inr(position.summary.amount)}`}</div>
        </div>
      )}
      <div className="space-y-4">
        <Field label="From (who paid)"><select data-testid="settlement-from-select" className="hd-input" value={f.from_person_id} onChange={up("from_person_id")}><option value="">Select person…</option>{active.map(opt)}</select></Field>
        <Field label="To (who received)"><select data-testid="settlement-to-select" className="hd-input" value={f.to_person_id} onChange={up("to_person_id")}><option value="">Select person…</option>{active.filter((m) => m.person_id !== f.from_person_id).map(opt)}</select></Field>
        <Field label="Amount (₹)"><input data-testid="settlement-amount-input" className="hd-input text-lg font-bold" inputMode="decimal" value={f.amount} onChange={up("amount")} placeholder="100000" /></Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Date"><input data-testid="settlement-date-input" type="date" className="hd-input" value={f.date} onChange={up("date")} /></Field>
          <Field label="Payment mode"><select data-testid="settlement-mode-select" className="hd-input" value={f.payment_mode} onChange={up("payment_mode")}>{MODES.map((m) => <option key={m}>{m}</option>)}</select></Field>
        </div>
        <Field label="Reference (UTR / cheque no.)"><input data-testid="settlement-reference-input" className="hd-input" value={f.reference} onChange={up("reference")} /></Field>
        <Field label="Related holding (optional)">
          <select data-testid="settlement-holding-select" className="hd-input" value={f.holding_id} onChange={up("holding_id")}>
            <option value="">Not linked to a specific holding</option>
            {holdings.map((h) => <option key={h.id} value={h.id}>{h.asset.name}</option>)}
          </select>
        </Field>
        <Field label="Notes"><textarea data-testid="settlement-notes-input" className="hd-input h-20 py-3" value={f.notes} onChange={up("notes")} /></Field>
        {editId && <Field label="Reason for edit"><input data-testid="settlement-edit-comment-input" className="hd-input" value={editComment} onChange={(e) => setEditComment(e.target.value)} /></Field>}
        <div className="hd-card">
          <div className="hd-label mb-2">Supporting screenshot (optional)</div>
          <select className="hd-input h-11 text-sm mb-2" value={docType} onChange={(e) => setDocType(e.target.value)} data-testid="settlement-doc-type-select">{DOC_TYPES.map((t) => <option key={t}>{t}</option>)}</select>
          <input data-testid="settlement-file-input" type="file" className="text-sm" onChange={(e) => setFile(e.target.files?.[0] || null)} />
        </div>
        {f.from_person_id && f.to_person_id && (
          <div className="text-xs text-[#718096]" data-testid="settlement-confirmation-note">
            {cross ? "This is between the two sides — the other Karta will need to confirm it." : "Both people are on the same side — this is recorded as confirmed."}
          </div>
        )}
        <button className="hd-btn-primary w-full" disabled={busy || !f.from_person_id || !f.to_person_id || !f.amount} onClick={submit} data-testid="settlement-submit-button">
          {busy ? "Saving…" : editId ? "Save changes" : "Record settlement"}
        </button>
      </div>
    </div>
  );
}
